import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { ApiClientError, idempotencyKeyFor } from '@/lib/http-core';
import { EXPORT_COLUMN_KEYS } from '../lib/exportColumns';

/**
 * QA-433 — 주문 엑셀 다운로드 양식(고도몰 "다운로드 양식관리"). 담당자들이 업무별로 항목·순서를 정해 두고 공유한다.
 * BE 계약은 전달 문서(BO-TO-BE-2026-10-20-order-export-templates.md) 제안안 — order-service 저장.
 */
export interface OrderExportTemplate {
  templateId: string;
  name: string;
  /** 엑셀 칸 키(주문 엑셀 ExportColumn key) — 이 순서대로 칸이 나온다. */
  columns: string[];
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface OrderExportTemplateInput {
  name: string;
  columns: string[];
}

/** 양식명 최대 길이(고도몰 30자와 같음). */
export const TEMPLATE_NAME_MAX = 30;

const BASE = '/api/admin/v1/orders/export-templates';
export const orderExportTemplatesKey = ['admin', 'orders', 'export-templates'] as const;

/** BE 반영 전(라우트 없음) — 코드 없는 404/405. */
export class ExportTemplatesNotReadyError extends Error {}

const throwFrom = async (res: Response): Promise<never> => {
  if (res.status === 405) throw new ExportTemplatesNotReadyError('not ready');
  const err = await errorFromResponse(res);
  const code = err instanceof ApiClientError ? String(err.code) : '';
  if (res.status === 404 && (code === '' || code.startsWith('HTTP_') || code === 'COMMON_NOT_FOUND')) {
    throw new ExportTemplatesNotReadyError('not ready');
  }
  throw err;
};

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const KNOWN = new Set(EXPORT_COLUMN_KEYS);

const normalize = (raw: Record<string, unknown>): OrderExportTemplate => ({
  templateId: String(raw.templateId ?? raw.id ?? ''),
  name: String(raw.name ?? ''),
  // 모르는 칸 키(BE 가 새로 추가했지만 BO 가 아직 모르는 칸)는 화면에서 고를 수 없어 걸러 둔다.
  columns: Array.isArray(raw.columns) ? raw.columns.filter((c): c is string => typeof c === 'string' && KNOWN.has(c)) : [],
  createdBy: str(raw.createdBy),
  createdAt: str(raw.createdAt),
  updatedAt: str(raw.updatedAt),
});

export const useOrderExportTemplatesQuery = (enabled = true) =>
  useQuery<OrderExportTemplate[]>({
    queryKey: orderExportTemplatesKey,
    enabled,
    retry: false,
    queryFn: async () => {
      const res = await adminFetch(BASE);
      if (!res.ok) await throwFrom(res);
      const env = (await res.json()) as { data?: unknown };
      const list = Array.isArray(env.data)
        ? env.data
        : Array.isArray((env.data as { items?: unknown } | null)?.items)
          ? (env.data as { items: unknown[] }).items
          : [];
      return (list as Record<string, unknown>[]).map(normalize);
    },
  });

export const useOrderExportTemplateMutations = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: orderExportTemplatesKey });

  const save = useMutation({
    mutationFn: async ({ templateId, input }: { templateId: string | null; input: OrderExportTemplateInput }) => {
      const path = templateId ? `${BASE}/${encodeURIComponent(templateId)}` : BASE;
      const method = templateId ? 'PUT' : 'POST';
      const res = await adminFetch(path, {
        method,
        headers: { 'Idempotency-Key': idempotencyKeyFor(method, path, input) },
        body: JSON.stringify(input),
      });
      if (!res.ok) await throwFrom(res);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (templateId: string) => {
      const res = await adminFetch(`${BASE}/${encodeURIComponent(templateId)}`, { method: 'DELETE' });
      if (!res.ok) await throwFrom(res);
    },
    onSuccess: invalidate,
  });

  return { save, remove };
};
